'use client'

import { create } from 'zustand'
import { z } from 'zod'

import {
  chatIn,
  materialsIn,
  normalizeStudioCollection,
  STUDIO_PATH,
} from '@/features/studio-renew/lib/studio-read'
import { studioCollectionSchemas } from '@/features/studio-renew/schemas/studio-read.schema'
import type {
  ServerMaterial,
  ServerWork,
} from '@/features/studio-renew/lib/studio-read'
import type { StudioCollectionResponse } from '@/features/studio-renew/schemas/studio-read.schema'

import { normalizeHabitIds } from '@/features/studio-renew/lib/character-habits'
import { characterForbidden } from '@/features/studio-renew/lib/character-forbidden'
import {
  compareSaveBodies,
  sameSaveValue,
} from '@/features/studio-renew/lib/save-conflict'
import { isStudioWorkLocked } from '@/features/studio-renew/lib/publication'
import {
  internalReviewSchema,
  publicationSchema,
} from '@/features/studio-renew/schemas/publication.schema'
import { ApiError, api } from '@/features/veluvit/services/api'
import {
  isDataUrl,
  rememberAssets,
  uploadDataUrl,
} from '@/features/studio-renew/media'
import { isBlankDraft, useRenewStore } from '@/features/studio-renew/store'
import {
  normalizeOfficialCommands,
  SERVICE_COMMAND_KEYS,
} from '@/features/studio-renew/official-commands'
import {
  emptyWork,
  hasWorkValues,
  WORK_DRAFT_ID,
} from '@/features/studio-renew/lib/work-draft'
import {
  emptyMaterial,
  isMaterialDraftId,
  readMaterialDraftId,
  rememberMaterialDraftId,
} from '@/features/studio-renew/lib/material-draft'

import type {
  SaveBody,
  SaveChoice,
} from '@/features/studio-renew/lib/save-conflict'
import type { MediaAsset } from '@/features/studio-renew/media'
import type { Kind, StudioData } from '@/features/studio-renew/store'
import type {
  Character,
  Chat,
  Command,
  ImageCrop,
  Scene,
  StatusBoard,
  StatusField,
  World,
} from '@/features/studio-renew/types'
import type { MaterialKind } from '@/features/studio-renew/lib/material-draft'

/* 새 스튜디오 서버 동기화(2026-10-12) — 화면은 store 액션만 부르고, 이 모듈이 store 의 변화를 지켜보다가 서버에 저장한다.
   서버 경로 · 검증: sai-backend veluvit/creator/renew_views.py · renew_schema.py · renew_work.py, 요청 문서 backend.md §5.

   - 불러오기: 회원 확인 뒤 loadStudio() 는 내 작품만 받는다. 재료와 보조 목록은 해당 탭·편집에 들어갈 때 받는다.
   - 저장: 서버가 발급한 id의 엔티티는 입력이 멈추고 3초 뒤 PATCH(5-8). 새 작품과 새 재료는 로컬 폼이며 값이 생긴 뒤 최초 POST하고 실제 id로 승격한다.
     작품이 아직 서버에 없는 재료를 가리키면 그 재료를 먼저 만든다(서버가 남의 · 없는 id 를 거절한다).
   - id: 실제 엔티티는 서버 id를 쓴다. new(작품)와 등록한 draft-UUID(재료)는 작성 의도이며 서버 리소스 URL로 보내지 않는다. 그 외 예전 alias 엔티티는 자동 저장하지 않는다.
   - 이미지: data URL 은 보내기 전에 올리고 주소로 바꾼다(media.ts).
   - 실패는 자동으로 다시 보내지 않는다 — 상태(useSyncStatus)에 남기고, 다음 입력이나 ‘다시 시도’(retryFailed) 때 보낸다.
   - 서버가 아직 프론트와 다르게 거절하는 값(backend.md 5-1)은 보낼 때 서버가 받는 모양으로 맞춘다 — 각 변환 함수 주석 참고. */

const DEBOUNCE_MS = 3000
const KINDS: Kind[] = ['world', 'character', 'status', 'command', 'chat']
const COLLECTION = {
  world: 'worlds',
  character: 'characters',
  status: 'statuses',
  command: 'commands',
  chat: 'chats',
} as const satisfies Record<Kind, keyof StudioData>
const PATH = STUDIO_PATH

/** 저장 진행과 항목별 복구 상태. 충돌은 재전송 대신 최신 저장본과 비교한다. */
export type StudioSaveIssue = {
  kind: Kind
  id: string
  label: string
  message: string
  conflict: boolean
}
type SyncStatus = {
  saving: number
  savedAt: number | null
  error: string | null
  issues: Record<string, StudioSaveIssue>
}
export const useSyncStatus = create<SyncStatus>(() => ({
  saving: 0,
  savedAt: null,
  error: null,
  issues: {},
}))
export const useWorkDraftStatus = create<{
  owner: string | null
  serverId: string | null
  intent: string | null
}>(() => ({ owner: null, serverId: null, intent: null }))
type CollectionLoad = 'idle' | 'loading' | 'ready' | 'error'
export const useStudioCollectionStatus = create<{
  byKind: Record<Kind, CollectionLoad>
  error: Partial<Record<Kind, string>>
}>(() => ({
  byKind: {
    world: 'idle',
    character: 'idle',
    status: 'idle',
    command: 'idle',
    chat: 'idle',
  },
  error: {},
}))

type Entry = {
  timer?: ReturnType<typeof setTimeout>
  /** 마지막 저장 뒤 바뀌었다 */
  dirty: boolean
  /** 다른 엔티티가 가리켜서 비어 있어도 서버에 있어야 한다 */
  needed: boolean
  failed: boolean
  uncertain?: boolean
  conflict?: boolean
  flight?: Promise<void>
}

let owner: string | null = null
let generation = 0
let loadingFailed = false
let failedKinds: Kind[] = []
const collectionFlights = new Map<string, Promise<void>>()
const collectionSerial = new Map<Kind, number>()
const readSerial = new Map<Kind, number>()
const supportFlights = new Map<string, Promise<void>>()
const supportReady = new Set<string>()
/** `${kind}:${로컬 id}` → 서버 id. 불러온 것은 같은 값(서버 id 그대로 쓴다) */
let ids: Record<string, string> = {}
const entries = new Map<string, Entry>()
/** 목록 요청보다 나중에 채택한 저장본은 오래된 목록으로 교체하지 않는다. */
const adoptedVersions = new Map<string, number>()
const cacheRefreshKinds = new Set<Kind>()
const markAdopted = (key: string) => {
  adoptedVersions.set(key, (adoptedVersions.get(key) ?? 0) + 1)
  const reader = collectionReader
  const kind = key.slice(0, key.indexOf(':')) as Kind
  if (!reader || cacheRefreshKinds.has(kind)) return
  cacheRefreshKinds.add(kind)
  queueMicrotask(() => {
    cacheRefreshKinds.delete(kind)
    if (
      collectionReader === reader &&
      useRenewStore.getState().owner === reader.owner
    )
      void reader.read(kind).catch(() => {})
  })
}
type Pending = {
  base?: Record<string, unknown>
  conflict?: boolean
  uncertain?: boolean
  deleted?: boolean
  creating?: boolean
  soft?: boolean
  serverId?: string
  intent?: string
}
let pending: Record<string, Pending> = {}
/** 저장본을 읽어 실제 충돌인지 판정 중인 항목. 판정이 끝나기 전에는 화면에 알리지 않는다 */
const settlingConflicts = new Set<string>()
const workDraftMark = (patch: Partial<Pending> = {}): Pending => ({
  soft: true,
  intent: pending[`chat:${WORK_DRAFT_ID}`]?.intent,
  ...patch,
})
export const workDraftIntent = () => pending[`chat:${WORK_DRAFT_ID}`]?.intent
/** 서버 공식 명령어 키(renew_work.py OFFICIAL_COMMANDS) — 불러올 때 /studio/command/official/ 로 갱신한다 */
const DEFAULT_OFFICIAL_KEYS: readonly string[] = SERVICE_COMMAND_KEYS
let officialKeys = new Set(DEFAULT_OFFICIAL_KEYS)
/** 서버에서 받은 값을 store 에 넣는 동안은 변화로 치지 않는다 */
let applying = false
let unsubscribe: (() => void) | null = null

const keyOf = (kind: Kind, id: string) => `${kind}:${id}`
type Context = { owner: string; generation: number }
const current = (context: Context) =>
  owner === context.owner &&
  useRenewStore.getState().owner === context.owner &&
  generation === context.generation
const contextOf = (): Context | null => (owner ? { owner, generation } : null)
export function resetStudioSync() {
  owner = null
  collectionReader = null
  generation += 1
  ids = {}
  pending = {}
  for (const entry of entries.values()) clearTimeout(entry.timer)
  entries.clear()
  adoptedVersions.clear()
  cacheRefreshKinds.clear()
  collectionFlights.clear()
  collectionSerial.clear()
  readSerial.clear()
  supportFlights.clear()
  supportReady.clear()
  useWorkDraftStatus.setState({ owner: null, serverId: null, intent: null })
  useSyncStatus.setState({ saving: 0, savedAt: null, error: null, issues: {} })
  useStudioCollectionStatus.setState({
    byKind: {
      world: 'idle',
      character: 'idle',
      status: 'idle',
      command: 'idle',
      chat: 'idle',
    },
    error: {},
  })
}
const serverId = (kind: Kind, id?: string | null) =>
  id ? ids[keyOf(kind, id)] : undefined
/** 바깥에 내보낼 서버 id(작품 링크 등) — 표에 없으면 화면 id 가 곧 서버 id 다 */
export const studioServerId = (kind: Kind, id: string) => {
  const draft = useWorkDraftStatus.getState()
  if (
    kind === 'chat' &&
    id === WORK_DRAFT_ID &&
    draft.owner === useRenewStore.getState().owner
  )
    return draft.serverId ?? id
  return serverId(kind, id) ?? id
}
const entity = (kind: Kind, id: string) =>
  (useRenewStore.getState()[COLLECTION[kind]] as Record<string, unknown>)[id]

export const isLocalMaterialDraft = (kind: Kind, id: string) =>
  kind !== 'chat' &&
  isMaterialDraftId(id) &&
  pending[keyOf(kind, id)]?.soft === true

/** 빈 새 재료를 선택한 것만으로 빈 작품이 생성·목록 노출되지 않게 한다. */
export function hasWorkDraftInput(work: Chat): boolean {
  const entered = (kind: MaterialKind, id: string | null) => {
    if (!id) return false
    if (!isMaterialDraftId(id)) return true
    const item = entity(kind, id)
    return !!item && !isBlankDraft(kind, item)
  }
  return hasWorkValues({
    ...work,
    characterIds: work.characterIds.filter((id) => entered('character', id)),
    worldId: entered('world', work.worldId) ? work.worldId : null,
    statusId: entered('status', work.statusId) ? work.statusId : null,
    commandIds: work.commandIds.filter((id) => entered('command', id)),
  })
}

/** 폼 진입은 로컬에서만 준비한다. 실제 입력 전에는 POST하지 않는다. */
export function beginMaterialDraft(
  kind: MaterialKind,
  patch: Record<string, unknown> = {},
  draftId = `draft-${crypto.randomUUID()}`
): string {
  const state = useRenewStore.getState()
  if (!owner || owner !== state.owner)
    throw new Error('스튜디오 계정을 확인해 주세요.')
  if (!isMaterialDraftId(draftId))
    throw new Error('새 작성 화면의 ID를 확인하지 못했어요.')
  if (entity(kind, draftId)) return draftId
  pending[keyOf(kind, draftId)] = { soft: true, intent: crypto.randomUUID() }
  entries.set(keyOf(kind, draftId), {
    dirty: false,
    needed: false,
    failed: false,
  })
  const collection = COLLECTION[kind]
  silently(() =>
    state.hydrate({
      worlds: state.worlds,
      characters: state.characters,
      statuses: state.statuses,
      commands: state.commands,
      chats: state.chats,
      [collection]: {
        ...state[collection],
        [draftId]: {
          ...emptyMaterial(kind),
          ...patch,
          id: draftId,
          updatedAt: Date.now(),
        },
      },
    })
  )
  return draftId
}

export async function savedMaterialId(
  kind: MaterialKind,
  requestedId: string
): Promise<string> {
  const context = contextOf()
  if (!context) throw new Error('스튜디오 계정을 확인해 주세요.')
  await flushAll([keyOf(kind, requestedId)])
  const id = studioServerId(kind, requestedId)
  if (!current(context) || isMaterialDraftId(id) || !serverId(kind, id))
    throw new Error('내용을 입력해 저장한 뒤 연결해 주세요.')
  return id
}

export function discardMaterialDraft(kind: MaterialKind, id: string) {
  if (!isLocalMaterialDraft(kind, id)) return
  const key = keyOf(kind, id)
  if (
    entries.get(key)?.flight ||
    pending[key]?.creating ||
    pending[key]?.uncertain
  )
    throw new Error('생성 결과를 확인한 뒤 정리해 주세요.')
  const state = useRenewStore.getState()
  const touched: [Kind, string][] = []
  for (const work of Object.values(state.chats))
    if (
      refsOf('chat', work).some(
        ([refKind, refId]) => refKind === kind && refId === id
      )
    )
      touched.push(['chat', work.id])
  if (kind === 'world')
    for (const character of Object.values(state.characters))
      if (character.worldId === id) touched.push(['character', character.id])
  clearTimeout(entries.get(key)?.timer)
  entries.delete(key)
  delete pending[key]
  silently(() => {
    state.remove(kind, id)
    if (kind === 'character') {
      const currentState = useRenewStore.getState()
      const chats = Object.fromEntries(
        Object.entries(currentState.chats).map(([key, work]) => [
          key,
          {
            ...work,
            starts: work.starts.map((start) => ({
              ...start,
              intro: {
                ...start.intro,
                lines: start.intro.lines.map((line) =>
                  line.charId === id ? { ...line, charId: undefined } : line
                ),
              },
            })),
          },
        ])
      )
      currentState.hydrate({
        worlds: currentState.worlds,
        characters: currentState.characters,
        statuses: currentState.statuses,
        commands: currentState.commands,
        chats,
      })
    }
  })
  for (const [refKind, refId] of touched) {
    const refKey = keyOf(refKind, refId)
    pending[refKey] ??=
      refKind === 'chat' && refId === WORK_DRAFT_ID ? workDraftMark() : {}
    const e = entry(refKind, refId)
    e.dirty = true
    if (!e.flight) schedule(refKind, refId)
  }
  clearSaveIssue(kind, id)
  refreshError()
}

/** 빈 작품은 이 기기에서만 만든다. 이전 미확정 입력이 있으면 이어서 편집한다. */
export function beginWorkDraft(): string {
  const state = useRenewStore.getState()
  if (!owner || owner !== state.owner)
    throw new Error('스튜디오 계정을 확인해 주세요.')
  if (state.chats[WORK_DRAFT_ID]) return WORK_DRAFT_ID
  const key = keyOf('chat', WORK_DRAFT_ID)
  if (pending[key]?.uncertain || pending[key]?.creating)
    throw new Error('이전 작품의 생성 결과를 먼저 확인해 주세요.')
  clearTimeout(entries.get(key)?.timer)
  entries.delete(key)
  const intent = crypto.randomUUID()
  useWorkDraftStatus.setState({ owner, serverId: null, intent })
  pending[key] = { soft: true, intent }
  silently(() =>
    state.hydrate({
      worlds: state.worlds,
      characters: state.characters,
      statuses: state.statuses,
      commands: state.commands,
      chats: {
        ...state.chats,
        [WORK_DRAFT_ID]: {
          ...emptyWork(),
          id: WORK_DRAFT_ID,
          updatedAt: Date.now(),
        },
      },
    })
  )
  refreshError()
  return WORK_DRAFT_ID
}

export function discardWorkDraft() {
  const context = contextOf()
  if (!context || !current(context)) return
  const key = keyOf('chat', WORK_DRAFT_ID)
  if (
    entries.get(key)?.flight ||
    pending[key]?.creating ||
    pending[key]?.uncertain
  )
    throw new Error('작품의 저장 결과를 확인한 뒤 정리해 주세요.')
  const state = useRenewStore.getState()
  const chats = { ...state.chats }
  delete chats[WORK_DRAFT_ID]
  clearTimeout(entries.get(key)?.timer)
  entries.delete(key)
  delete pending[key]
  silently(() =>
    state.hydrate({
      worlds: state.worlds,
      characters: state.characters,
      statuses: state.statuses,
      commands: state.commands,
      chats,
    })
  )
  refreshError()
}

export async function savedWorkId(id: string): Promise<string> {
  if (id !== WORK_DRAFT_ID) return id
  const context = contextOf()
  if (!context) throw new Error('스튜디오 계정을 확인해 주세요.')
  await flushAll([keyOf('chat', id)])
  const saved = useWorkDraftStatus.getState()
  if (!current(context) || saved.owner !== context.owner || !saved.serverId)
    throw new Error('작품에 내용을 입력하고 저장해 주세요.')
  return saved.serverId
}

function entry(kind: Kind, id: string) {
  const key = keyOf(kind, id)
  let e = entries.get(key)
  if (!e) {
    e = { dirty: false, needed: false, failed: false }
    entries.set(key, e)
  }
  return e
}

function silently(fn: () => void) {
  applying = true
  try {
    fn()
  } finally {
    applying = false
  }
}

const messageOf = (error: unknown) =>
  error instanceof Error && error.message ? error.message : '저장하지 못했어요.'

const KIND_NAME: Record<Kind, string> = {
  world: '세계관',
  character: '캐릭터',
  status: '상태창',
  command: '명령어',
  chat: '작품',
}

function labelOf(kind: Kind, id: string) {
  const item = entity(kind, id) as { name?: string; title?: string } | undefined
  const name = (item?.name ?? item?.title ?? '').trim()
  return name ? `${KIND_NAME[kind]} ‘${name}’` : `이름 없는 ${KIND_NAME[kind]}`
}

function saveFailure(kind: Kind, id: string, error: unknown) {
  if (error instanceof ApiError && error.code === 'SAVE_CONFLICT')
    return `${labelOf(kind, id)}의 서버 저장본이 달라졌어요. 편집은 유지했어요. 저장할 내용을 비교해 주세요.`
  return `${labelOf(kind, id)} 저장 실패 — ${messageOf(error)}`
}

function setSaveIssue(
  kind: Kind,
  id: string,
  message: string,
  conflict = false
) {
  useSyncStatus.setState((state) => ({
    issues: {
      ...state.issues,
      [keyOf(kind, id)]: {
        kind,
        id,
        label: labelOf(kind, id),
        message,
        conflict,
      },
    },
  }))
}
function clearSaveIssue(kind: Kind, id: string) {
  useSyncStatus.setState((state) => {
    const issues = { ...state.issues }
    delete issues[keyOf(kind, id)]
    return { issues }
  })
}
function refreshError(fallback?: string) {
  const failed = [...entries].some(
    ([key, e]) => e.failed && !settlingConflicts.has(key)
  )
  useSyncStatus.setState((s) => ({
    error: failed ? (fallback ?? s.error ?? '저장하지 못했어요.') : null,
  }))
}

function hasPendingWrites() {
  return Object.entries(pending).some(([key, mark]) => {
    if (!mark.soft || mark.uncertain || mark.creating) return true
    if (mark.serverId) return false
    const separator = key.indexOf(':')
    const kind = key.slice(0, separator) as Kind
    const item = entity(kind, key.slice(separator + 1))
    return (
      !!item &&
      (kind === 'chat'
        ? hasWorkDraftInput(item as Chat)
        : !isBlankDraft(kind, item))
    )
  })
}

/* ---------- 보내는 모양(store → 서버) ---------- */

/** 이 엔티티가 가리키는 다른 엔티티 — 서버에 먼저 있어야 한다 */
function refsOf(kind: Kind, item: unknown): [Kind, string][] {
  if (kind === 'character') {
    const c = item as Character
    return c.worldMode === 'world' && c.worldId ? [['world', c.worldId]] : []
  }
  if (kind !== 'chat') return []
  const c = item as Chat
  return [
    ...c.characterIds.map((id): [Kind, string] => ['character', id]),
    ...(c.worldId ? [['world', c.worldId] as [Kind, string]] : []),
    ...(c.statusId ? [['status', c.statusId] as [Kind, string]] : []),
    ...c.commandIds.map((id): [Kind, string] => ['command', id]),
  ]
}

/** 이미지 칸의 data URL 을 올리고 store 의 값도 주소로 바꿔 둔다(다음 저장 때 다시 올리지 않게) */
async function uploadImages(kind: Kind, id: string, context: Context) {
  const item = entity(kind, id)
  if (!item) return
  const found = new Map<string, 'cover' | 'titleImage'>()
  const add = (v?: string, purpose: 'cover' | 'titleImage' = 'cover') => {
    if (isDataUrl(v) && (!found.has(v) || purpose === 'titleImage'))
      found.set(v, purpose)
  }
  if (kind === 'world') add((item as World).cover)
  if (kind === 'character') {
    add((item as Character).image)
    add((item as Character).avatar)
    add((item as Character).imageCrop?.src)
    add((item as Character).avatarCrop?.src)
    for (const shot of (item as Character).album ?? []) add(shot.image)
  }
  if (kind === 'chat') {
    const c = item as Chat
    add(c.cover)
    for (const cover of c.covers ?? []) add(cover)
    add(c.introGif)
    add(c.titleImage, 'titleImage')
    for (const place of c.albumPlaces)
      for (const scene of place.scenes) add(scene.image)
    for (const start of c.starts)
      for (const line of start.intro.lines) add(line.image)
  }
  if (!found.size) return
  const urls = new Map(
    await Promise.all(
      [...found].map(
        async ([src, purpose]) =>
          [src, await uploadDataUrl(src, context.owner, purpose)] as const
      )
    )
  )
  if (!current(context)) return
  const swap = (v?: string) => (v && urls.get(v)) || v || ''
  const store = useRenewStore.getState()
  // 올리는 사이에 바뀌었을 수 있어 지금 값을 다시 읽어 바꾼다
  silently(() => {
    const w =
      kind === 'world' ? (entity(kind, id) as World | undefined) : undefined
    if (w) store.updateWorld(id, { cover: swap(w.cover) })
    if (kind === 'character') {
      const c = entity(kind, id) as Character | undefined
      const crop = (value?: ImageCrop | null) =>
        value ? { ...value, src: swap(value.src) } : value
      if (c)
        store.updateCharacter(id, {
          image: swap(c.image),
          avatar: c.avatar ? swap(c.avatar) : c.avatar,
          imageCrop: crop(c.imageCrop),
          avatarCrop: crop(c.avatarCrop),
          album: c.album?.map((s) => ({ ...s, image: swap(s.image) })),
        })
    }
    if (kind === 'chat') {
      const c = entity(kind, id) as Chat | undefined
      if (c)
        store.updateChat(id, {
          cover: swap(c.cover),
          covers: c.covers?.map(swap),
          ...(c.introGif !== undefined ? { introGif: swap(c.introGif) } : {}),
          titleImage: swap(c.titleImage),
          albumPlaces: c.albumPlaces.map((p) => ({
            ...p,
            scenes: p.scenes.map((s) => ({ ...s, image: swap(s.image) })),
          })),
          starts: c.starts.map((s) => ({
            ...s,
            intro: {
              ...s.intro,
              lines: s.intro.lines.map((l) =>
                l.image ? { ...l, image: swap(l.image) } : l
              ),
            },
          })),
        })
    }
  })
}

const finite = (v: unknown) =>
  typeof v === 'number' && Number.isFinite(v) ? v : undefined

/**
 * 상태 항목 — 숫자형 시작값만 서버 타입으로 바꾼다. 나머지 잘못된 입력은 명시적으로 실패해 로컬 원본을 보존한다.
 */
function fieldsOut(fields: StatusField[]) {
  const seen = new Set<string>()
  return fields.map((f) => {
    if (!f.key.trim() || seen.has(f.key))
      throw new Error(
        '상태창 항목 이름이 비었거나 중복됐어요. 확인한 뒤 다시 저장해 주세요.'
      )
    seen.add(f.key)
    const base = { ...f, rule: f.rule ?? '', hidden: !!f.hidden }
    if (f.type === 'number') {
      const min = finite(f.min)
      const max = finite(f.max)
      const parsed = Number(f.initial)
      if (f.initial.trim() && !Number.isFinite(parsed))
        throw new Error(`상태창 ‘${f.key}’의 시작값을 숫자로 적어 주세요.`)
      const initial = f.initial.trim() ? parsed : (min ?? 0)
      if (
        (min !== undefined && initial < min) ||
        (max !== undefined && initial > max)
      )
        throw new Error(`상태창 ‘${f.key}’의 시작값이 범위를 벗어났어요.`)
      return { ...base, initial, min: min ?? null, max: max ?? null }
    }
    if (f.type === 'list') {
      const options = f.options
      if (
        !options.length ||
        options.some((option) => !option.trim()) ||
        new Set(options).size !== options.length
      )
        throw new Error(`상태창 ‘${f.key}’의 선택지를 확인해 주세요.`)
      if (!options.includes(f.initial))
        throw new Error(`상태창 ‘${f.key}’의 시작값을 선택지에서 골라 주세요.`)
      return { ...base, options, initial: f.initial, ordered: !!f.ordered }
    }
    return { ...base, initial: f.initial ?? '' }
  })
}

/** 장면 — 서버는 conditions · guide · entry 가 꼭 있어야 한다(5-1 f). 열림 조건 · 장면 시작은 편집 화면에서 없앴으니 옛 값도 비운다 */
const sceneOut = (s: Scene) => ({
  ...s,
  conditions: [],
  guide: { situation: s.guide?.situation ?? '' },
  entry: { enabled: false, prologue: '' },
})

function bodyOf(
  kind: Kind,
  item: unknown,
  references: 'local' | 'server' = 'local'
): Record<string, unknown> {
  if (kind === 'world') {
    const w = item as World
    if (
      w.keywords.some(
        (keyword) =>
          keyword.keys.length === 0 || keyword.keys.some((key) => !key.trim())
      )
    )
      throw new Error('세계관 키워드북에 비어 있는 키워드가 있어요.')
    return {
      name: w.name,
      summary: w.summary,
      cover: w.cover,
      body: w.body,
      keywords: w.keywords,
      places: w.places,
      visibility: w.visibility,
    }
  }
  if (kind === 'character') {
    const c = item as Character
    const forbidden = characterForbidden(c.forbidden ?? [], c.name)
    if (forbidden.error) throw new Error(forbidden.error)
    // 세계관을 아직 고르지 않았으면 연결 없음으로 저장한다. 고르는 중이라고 이름 · 설정 저장까지 막지 않는다.
    const linksWorld = c.worldMode === 'world' && !!c.worldId
    const worldId = linksWorld
      ? references === 'server'
        ? c.worldId
        : serverId('world', c.worldId)
      : undefined
    if (linksWorld && !worldId)
      throw new Error('캐릭터에 연결한 세계관을 먼저 저장해 주세요.')
    return {
      image: c.image,
      // 채팅 프로필 사진 — 서버는 모르는 칸도 재료에 그대로 둔다. 공개본 cast 로 내보내는 건 서버 몫(backend9.md 6)
      avatar: c.avatar ?? '',
      // 자르기 원본 · 자리 — 서버는 모르는 칸이라 재료 JSON 에 그대로 남는다(다시 자르기용)
      imageCrop: c.imageCrop ?? null,
      avatarCrop: c.avatarCrop ?? null,
      name: c.name,
      tagline: c.tagline,
      persona: c.persona,
      forbidden: forbidden.values,
      habits: normalizeHabitIds(c.id, c.habits),
      worldMode: c.worldMode === 'world' && !linksWorld ? 'none' : c.worldMode,
      worldId: worldId ?? null,
      worldNote: c.worldNote,
      album: c.album ?? [],
    }
  }
  if (kind === 'status') {
    const s = item as StatusBoard
    return {
      name: s.name,
      fields: fieldsOut(s.fields),
      html: s.html,
      design: s.design ?? null,
    }
  }
  if (kind === 'command') {
    const c = item as Command
    if (c.key === '!호감')
      throw new Error('종료된 벨루빗 명령어예요. 다른 키를 사용해 주세요.')
    return {
      key: c.key,
      title: c.title,
      description: c.description,
      html: c.html,
    }
  }
  const c = item as Chat
  const officialCommands = normalizeOfficialCommands(c.officialCommands)
  const requiredId = (refKind: Kind, localId: string) => {
    const sid = references === 'server' ? localId : serverId(refKind, localId)
    if (!sid) throw new Error('작품에 연결한 재료를 먼저 저장해 주세요.')
    return sid
  }
  const characterIds = c.characterIds.map((x) => requiredId('character', x))
  if (c.lead && !c.characterIds.includes(c.lead))
    throw new Error('주인공을 출연 캐릭터에서 다시 골라 주세요.')
  if (officialCommands.some((key) => !officialKeys.has(key)))
    throw new Error(
      '현재 서버에서 제공하지 않는 공식 명령어가 있어요. 작품 설정을 확인해 주세요.'
    )
  // 초안은 미선택 등급(null)도 저장한다. 필수 입력 검사는 명시적 저장 버튼에서 표시한다.
  return {
    name: c.name,
    cover: c.covers?.[0] ?? c.cover ?? '',
    covers: c.covers ?? (c.cover ? [c.cover] : []),
    ...(c.introGif !== undefined ? { introGif: c.introGif } : {}),
    titleImage: c.titleImage ?? '',
    characterIds,
    lead: c.lead ? requiredId('character', c.lead) : null,
    maxSpeakers: c.maxSpeakers,
    worldId: c.worldId ? requiredId('world', c.worldId) : null,
    starts: c.starts.map((s) => ({
      ...s,
      intro: {
        context: s.intro.context,
        lines: s.intro.lines.map((line) => {
          // backend10 완료 계약: 사진은 원래 image 종류로 보내며 상황 지문으로 위장하지 않는다.
          if (line.who === 'image')
            return {
              id: line.id,
              who: 'image',
              text: '',
              image: line.image ?? '',
            }
          if (!line.charId) return line
          const sid = requiredId('character', line.charId)
          if (!characterIds.includes(sid))
            throw new Error('시작 대사의 화자가 출연 캐릭터에 없어요.')
          return { ...line, charId: sid }
        }),
      },
      suggestions: s.suggestions,
    })),
    playGuide: c.playGuide,
    statusId: c.statusId ? requiredId('status', c.statusId) : null,
    commandIds: c.commandIds.map((x) => requiredId('command', x)),
    officialCommands,
    situation: c.situation ?? '',
    narrator: c.narrator ?? '',
    difficulty: c.difficulty ?? 'normal',
    pace: c.pace ?? 'natural',
    places: c.places ?? [],
    albumOn: c.albumOn,
    albumPlaces: c.albumPlaces
      .filter((p) => p.name.trim() || p.scenes.length > 0)
      .map((p) => ({ ...p, scenes: p.scenes.map(sceneOut) })),
    description: c.description,
    genre: c.genre,
    tags: c.tags,
    target: c.target,
    audience: c.audience,
    visibility: c.visibility,
    consent: c.consent,
    commentsOn: c.commentsOn !== false,
  }
}

/* ---------- 받는 모양(서버 → store) ---------- */

const createdMaterialSchema = z.looseObject({ id: z.string().min(1) })
const createdWorkSchema = z.looseObject({
  id: z.string().min(1),
  work: z.record(z.string(), z.unknown()),
  version: z.number(),
  published: z.boolean(),
  reviewStatus: z.string(),
})

function projectCreated(kind: Kind, response: unknown) {
  const local = (_refKind: Kind, sid: string) => sid
  if (kind === 'chat') {
    const row = createdWorkSchema.parse(response) as ServerWork
    return { id: row.id, item: chatIn(row, local) }
  }
  const row = createdMaterialSchema.parse(response) as ServerMaterial
  const shape =
    kind === 'character'
      ? (value: Record<string, unknown>) => ({
          ...value,
          worldId:
            typeof value.worldId === 'string'
              ? local('world', value.worldId)
              : null,
        })
      : kind === 'status'
        ? (value: Record<string, unknown>) => ({
            ...value,
            design: value.design ?? undefined,
            fields: ((value.fields as StatusField[] | undefined) ?? []).map(
              (field) => ({
                ...field,
                initial: String(field.initial ?? ''),
                options: field.options ?? [],
                min: field.min ?? undefined,
                max: field.max ?? undefined,
              })
            ),
          })
        : (value: Record<string, unknown>) => value
  return { id: row.id, item: materialsIn(kind, [row], local, shape)[row.id] }
}

/** 서버가 발급한 ID의 생성 응답만 화면 캐시에 채택한다. 생성 POST는 호출자가 완료한다. */
export function acceptCreated(kind: Kind, response: unknown): string {
  const state = useRenewStore.getState()
  if (!state.owner || state.owner !== owner)
    throw new Error('계정을 확인하지 못해 생성 결과를 적용하지 않았어요.')
  const { id, item } = projectCreated(kind, response)
  const key = keyOf(kind, id)
  if (pending[key])
    throw new Error('같은 ID에 미저장 편집이 있어 생성 결과를 덮지 않았어요.')
  ids[key] = id
  const previous = entries.get(key)
  clearTimeout(previous?.timer)
  entries.delete(key)
  markAdopted(key)
  clearSaveIssue(kind, id)
  const collection = COLLECTION[kind]
  silently(() =>
    state.hydrate({
      worlds: state.worlds,
      characters: state.characters,
      statuses: state.statuses,
      commands: state.commands,
      chats: state.chats,
      [collection]: { ...state[collection], [id]: item },
    })
  )
  return id
}

/** 불러올 때 버전이 달라도 실제로 같은 칸을 양쪽에서 고친 경우만 작가에게 묻는다. 나머지는 조용히 합친다. */
async function settleLoadedConflict(kind: Kind, id: string) {
  const key = keyOf(kind, id)
  if (settlingConflicts.has(key)) return
  settlingConflicts.add(key)
  const context = contextOf()
  try {
    const comparison = withLatestEdit(await inspectSaveConflict(kind, id))
    if (!comparison.conflicts.length) await resolveSaveConflict(comparison, {})
  } catch {
    /* 저장본을 읽지 못하면 초안과 충돌 상태를 유지하고 비교 버튼에서 다시 읽는다. */
  }
  settlingConflicts.delete(key)
  if (!context || !current(context) || !entries.get(key)?.conflict) return
  const message = `${labelOf(kind, id)}의 미저장 편집과 최신 저장본을 비교해 주세요.`
  setSaveIssue(kind, id, message, true)
  refreshError(message)
}

/** 자동 판정 중에도 작가는 계속 입력한다. 읽어 온 저장본은 두고 내 편집만 지금 값으로 다시 비교한다 */
function withLatestEdit(
  comparison: StudioSaveComparison
): StudioSaveComparison {
  const { kind, id, server } = comparison
  const item = entity(kind, id)
  if (!item) return comparison
  const local = bodyOf(kind, item)
  return {
    ...comparison,
    local,
    localRevision: (item as { revision?: string }).revision,
    ...compareSaveBodies(pending[keyOf(kind, id)]?.base, local, server),
  }
}

/** 충돌 비교는 읽기만 한다. 과거 이식 영수증 대신 현재 서버 저장본을 가져온다. */
export async function inspectSaveConflict(kind: Kind, id: string) {
  const context = contextOf()
  if (!context || !current(context) || serverId(kind, id) !== id)
    throw new Error('편집할 계정과 항목을 다시 확인해 주세요.')
  const readVersion = adoptedVersions.get(keyOf(kind, id)) ?? 0
  const raw = await api<Record<string, unknown>>(`${PATH[kind]}${id}/`)
  if (!current(context))
    throw new Error('계정이 바뀌어 저장본 비교를 중단했어요.')
  if ((adoptedVersions.get(keyOf(kind, id)) ?? 0) !== readVersion)
    throw new Error('저장본이 갱신됐어요. 최신 내용으로 다시 비교해 주세요.')
  const projected = projectCreated(kind, raw)
  if (projected.id !== id || typeof raw.revision !== 'string' || !raw.revision)
    throw new Error(
      '최신 저장본의 버전을 확인하지 못했어요. 다시 불러와 주세요.'
    )
  const item = entity(kind, id)
  if (!item) throw new Error('편집 중인 내용을 찾지 못했어요.')
  const local = bodyOf(kind, item)
  const server = bodyOf(kind, projected.item, 'server')
  const comparison = compareSaveBodies(
    pending[keyOf(kind, id)]?.base,
    local,
    server
  )
  return {
    context,
    kind,
    id,
    label: labelOf(kind, id),
    raw,
    localRevision: (item as { revision?: string }).revision,
    local,
    server,
    ...comparison,
  }
}
export type StudioSaveComparison = Awaited<
  ReturnType<typeof inspectSaveConflict>
>

async function adoptConflict(
  comparison: StudioSaveComparison,
  choices: Record<string, SaveChoice>
) {
  const { context, kind, id, raw, local, server, conflicts } = comparison
  const key = keyOf(kind, id)
  const assertUnchanged = () => {
    if (!current(context))
      throw new Error('계정이 바뀌어 편집 내용을 적용하지 않았어요.')
    const item = entity(kind, id)
    if (
      !pending[key] ||
      !item ||
      (item as { revision?: string }).revision !== comparison.localRevision ||
      !sameSaveValue(bodyOf(kind, item), local)
    )
      throw new Error(
        '비교하는 동안 편집 내용이 바뀌었어요. 최신 내용으로 다시 비교해 주세요.'
      )
  }
  assertUnchanged()
  if (
    conflicts.some(
      (field) => choices[field] !== 'local' && choices[field] !== 'server'
    )
  )
    throw new Error('달라진 항목마다 저장할 내용을 골라 주세요.')
  const merged = { ...comparison.merged }
  for (const field of conflicts)
    merged[field] = choices[field] === 'local' ? local[field] : server[field]
  assertUnchanged()
  const response =
    kind === 'chat'
      ? { ...raw, work: { ...(raw.work as SaveBody), ...merged } }
      : { ...raw, ...merged }
  const { item } = projectCreated(kind, response)
  const remote = projectCreated(kind, raw).item
  for (const [refKind, refId] of refsOf(kind, remote))
    ids[keyOf(refKind, refId)] = refId
  const needsSave = !sameSaveValue(merged, server)
  if (needsSave) pending[key] = { base: server }
  else delete pending[key]
  const e = entry(kind, id)
  e.conflict = false
  e.failed = false
  e.dirty = needsSave
  markAdopted(key)
  clearTimeout(e.timer)
  e.timer = undefined
  const nextItem = needsSave
    ? { ...(item as object), updatedAt: Date.now() }
    : item
  silently(() => {
    const state = useRenewStore.getState()
    const collection = COLLECTION[kind]
    state.hydrate({
      worlds: state.worlds,
      characters: state.characters,
      statuses: state.statuses,
      commands: state.commands,
      chats: state.chats,
      [collection]: { ...state[collection], [id]: nextItem },
    })
  })
  clearSaveIssue(kind, id)
  if (!hasPendingWrites()) useSyncStatus.setState({ savedAt: Date.now() })
  refreshError()
  // 자동 복구는 진행 중인 PATCH의 finally에서, 수동 복구는 아래 flushAll에서 저장한다.
}

export async function resolveSaveConflict(
  comparison: StudioSaveComparison,
  choices: Record<string, SaveChoice>
) {
  await adoptConflict(comparison, choices)
  if (!current(comparison.context)) return
  await flushAll([keyOf(comparison.kind, comparison.id)])
}

/** 완료 영수증은 저장 사실만 증명한다. 편집에 쓸 본문·revision은 최신 GET으로 일괄 갱신한다. */
export async function acceptImportedEntities(
  rows: { kind: Kind; response: unknown }[]
) {
  const context = contextOf()
  if (!context || !current(context))
    throw new Error('로그인한 작가 계정을 확인해 주세요.')
  const targets = rows.map(({ kind, response }) => ({
    kind,
    id: projectCreated(kind, response).id,
  }))
  await Promise.all(
    targets.map(({ kind, id }) => entries.get(keyOf(kind, id))?.flight)
  )
  if (!current(context))
    throw new Error('계정이 바뀌어 이식 결과를 적용하지 않았어요.')
  const latest = await Promise.all(
    targets.map(async ({ kind, id }) => {
      const readVersion = adoptedVersions.get(keyOf(kind, id)) ?? 0
      const raw = await api<Record<string, unknown>>(`${PATH[kind]}${id}/`)
      const projected = projectCreated(kind, raw)
      if (
        projected.id !== id ||
        typeof raw.revision !== 'string' ||
        !raw.revision
      )
        throw new Error(
          '이식은 저장됐지만 최신 편집 버전을 확인하지 못했어요. 저장 결과를 다시 확인해 주세요.'
        )
      return { kind, id, item: projected.item, readVersion }
    })
  )
  if (!current(context))
    throw new Error('계정이 바뀌어 이식 결과를 적용하지 않았어요.')
  const state = useRenewStore.getState()
  const next = {
    worlds: { ...state.worlds },
    characters: { ...state.characters },
    statuses: { ...state.statuses },
    commands: { ...state.commands },
    chats: { ...state.chats },
  }
  const preserved: string[] = []
  for (const { kind, id, item, readVersion } of latest) {
    const key = keyOf(kind, id)
    if ((adoptedVersions.get(key) ?? 0) !== readVersion) continue
    markAdopted(key)
    ids[key] = id
    for (const [refKind, refId] of refsOf(kind, item))
      ids[keyOf(refKind, refId)] = refId
    if (pending[key]) {
      preserved.push(labelOf(kind, id))
      if (entries.get(key)?.flight) continue
      pending[key] = { ...pending[key], conflict: true }
      const e = entry(kind, id)
      e.conflict = true
      e.failed = true
      clearTimeout(e.timer)
      e.timer = undefined
      setSaveIssue(
        kind,
        id,
        `${labelOf(kind, id)}의 이식은 완료됐어요. 이 기기에 남은 편집과 저장본을 비교해 주세요.`,
        true
      )
      continue
    }
    clearTimeout(entries.get(key)?.timer)
    entries.delete(key)
    clearSaveIssue(kind, id)
    Object.assign(next[COLLECTION[kind]], { [id]: item })
  }
  silently(() => useRenewStore.getState().hydrate(next))
  if (current(context))
    refreshError(
      preserved.length
        ? '이식은 완료됐어요. 남아 있는 편집 내용을 비교해 주세요.'
        : undefined
    )
  return preserved
}

/** 삭제 API 성공 응답 뒤 화면 캐시에서만 제거한다. 구독자의 자동 DELETE는 실행되지 않는다. */
export function acceptDeleted(kind: Kind, id: string): void {
  const state = useRenewStore.getState()
  if (
    !state.owner ||
    state.owner !== owner ||
    !id ||
    (serverId(kind, id) && serverId(kind, id) !== id)
  )
    throw new Error('계정이나 서버 ID를 확인하지 못했어요.')
  const collection = COLLECTION[kind]
  const nextCollection = { ...state[collection] }
  delete nextCollection[id]
  silently(() =>
    state.hydrate({
      worlds: state.worlds,
      characters: state.characters,
      statuses: state.statuses,
      commands: state.commands,
      chats: state.chats,
      [collection]: nextCollection,
    })
  )
  const key = keyOf(kind, id)
  clearTimeout(entries.get(key)?.timer)
  entries.delete(key)
  clearSaveIssue(kind, id)
  delete pending[key]
  for (const [alias, sid] of Object.entries(ids))
    if (alias.startsWith(`${kind}:`) && sid === id) delete ids[alias]
}

/** 재료 분리 응답의 새 재료와 바뀐 작품을 함께 적용한다. */
export function acceptDetached(
  kind: 'status' | 'command',
  response: { material: unknown; workId: string; work: unknown }
): { materialId: string; workId: string } {
  const state = useRenewStore.getState()
  if (!state.owner || state.owner !== owner)
    throw new Error('계정을 확인하지 못했어요.')
  const material = projectCreated(kind, response.material)
  const work = projectCreated('chat', response.work)
  if (work.id !== response.workId)
    throw new Error('분리 응답의 작품 ID가 일치하지 않아요.')
  if (pending[keyOf(kind, material.id)] || pending[keyOf('chat', work.id)])
    throw new Error('미저장 편집이 있어 분리 결과를 덮지 않았어요.')
  ids[keyOf(kind, material.id)] = material.id
  ids[keyOf('chat', work.id)] = work.id
  silently(() =>
    state.hydrate({
      worlds: state.worlds,
      characters: state.characters,
      statuses:
        kind === 'status'
          ? { ...state.statuses, [material.id]: material.item as StatusBoard }
          : state.statuses,
      commands:
        kind === 'command'
          ? { ...state.commands, [material.id]: material.item as Command }
          : state.commands,
      chats: { ...state.chats, [work.id]: work.item as Chat },
    })
  )
  return { materialId: material.id, workId: work.id }
}

/**
 * 계정의 스튜디오를 서버에서 불러와 store 를 채운다 — setOwner 다음에 부른다. 실패하면 던진다(회원 문이 ‘다시 시도’를 보인다).
 * 작품 목록에는 옛 스튜디오 작품도 섞여 온다 — 새 스튜디오 모양(characterIds 가 있는 것)이고 보관하지 않은 것만.
 */
function prepareStudioRead(nextOwner: string, collections: readonly Kind[]) {
  const sameOwner = owner === nextOwner
  const loadGeneration = sameOwner ? generation : ++generation
  const serial = new Map(
    collections.map((kind) => {
      const next = (collectionSerial.get(kind) ?? 0) + 1
      collectionSerial.set(kind, next)
      return [kind, next] as const
    })
  )
  const active = () =>
    owner === nextOwner &&
    useRenewStore.getState().owner === nextOwner &&
    generation === loadGeneration &&
    collections.every((kind) => collectionSerial.get(kind) === serial.get(kind))
  owner = nextOwner
  if (!sameOwner) {
    ids = {}
    pending = {}
  }
  if (!sameOwner) {
    useWorkDraftStatus.setState({
      owner: nextOwner,
      serverId: null,
      intent: null,
    })
    for (const e of entries.values()) clearTimeout(e.timer)
    entries.clear()
    adoptedVersions.clear()
    useSyncStatus.setState({
      saving: 0,
      savedAt: null,
      error: null,
      issues: {},
    })
    useStudioCollectionStatus.setState({
      byKind: {
        world: 'idle',
        character: 'idle',
        status: 'idle',
        command: 'idle',
        chat: 'idle',
      },
      error: {},
    })
    officialKeys = new Set(DEFAULT_OFFICIAL_KEYS)
  }
  if (!sameOwner) failedKinds = []
  const readVersions = new Map(adoptedVersions)
  for (const kind of collections)
    useStudioCollectionStatus.setState((state) => ({
      byKind: { ...state.byKind, [kind]: 'loading' },
      error: { ...state.error, [kind]: undefined },
    }))

  return { active, readVersions }
}

function captureStudioReadVersions() {
  return new Map(adoptedVersions)
}

export function beginStudioCollectionRead(nextOwner: string, kind: Kind) {
  const context = contextOf()
  const serial = (readSerial.get(kind) ?? 0) + 1
  readSerial.set(kind, serial)
  return {
    versions: captureStudioReadVersions(),
    current: () =>
      !!context &&
      context.owner === nextOwner &&
      current(context) &&
      readSerial.get(kind) === serial,
  }
}

export function acceptStudioCollections(
  nextOwner: string,
  values: Partial<Record<Kind, StudioCollectionResponse>>,
  versions?: Map<string, number>
) {
  if (useRenewStore.getState().owner !== nextOwner) return
  const collections = KINDS.filter((kind) => values[kind] !== undefined)
  if (!versions)
    for (const kind of collections)
      readSerial.set(kind, (readSerial.get(kind) ?? 0) + 1)
  const { active } = prepareStudioRead(nextOwner, collections)
  applyStudioCollections(
    nextOwner,
    collections,
    collections.map((kind) => [kind, values[kind]!] as const),
    versions ?? new Map(),
    active
  )
}

export function recordStudioCollectionError(kind: Kind, error: unknown) {
  useStudioCollectionStatus.setState((state) => ({
    byKind: {
      ...state.byKind,
      [kind]: state.byKind[kind] === 'ready' ? 'ready' : 'error',
    },
    error: { ...state.error, [kind]: messageOf(error) },
  }))
}

let collectionReader: {
  owner: string
  read: (kind: Kind) => Promise<void>
} | null = null
export function connectStudioReader(
  nextOwner: string,
  read: (kind: Kind) => Promise<void>
) {
  const reader = { owner: nextOwner, read }
  collectionReader = reader
  return () => {
    if (collectionReader === reader) collectionReader = null
  }
}

export async function loadStudio(
  nextOwner: string,
  collections: readonly Kind[] = ['chat']
) {
  if (collectionReader?.owner === nextOwner) {
    await Promise.all(collections.map((kind) => collectionReader!.read(kind)))
    return
  }
  const { active, readVersions } = prepareStudioRead(nextOwner, collections)
  const response = await Promise.all(
    collections.map(async (kind) => {
      const value = studioCollectionSchemas[kind].parse(
        await api<unknown>(PATH[kind])
      )
      return [kind, value] as const
    })
  ).catch((error: unknown) => {
    if (active()) {
      loadingFailed = true
      failedKinds = [...new Set([...failedKinds, ...collections])]
      useSyncStatus.setState({ savedAt: null, error: messageOf(error) })
      for (const kind of collections)
        useStudioCollectionStatus.setState((state) => ({
          byKind: { ...state.byKind, [kind]: 'error' },
          error: { ...state.error, [kind]: messageOf(error) },
        }))
    }
    throw error
  })
  applyStudioCollections(nextOwner, collections, response, readVersions, active)
}

function applyStudioCollections(
  nextOwner: string,
  collections: readonly Kind[],
  response: ReadonlyArray<readonly [Kind, StudioCollectionResponse]>,
  readVersions: Map<string, number>,
  active: () => boolean
) {
  if (!active()) return
  // 엔티티 URL과 저장 키는 서버가 발급한 id만 쓴다.
  const local = (kind: Kind, sid: string) => {
    ids[keyOf(kind, sid)] = sid
    return sid
  }

  const fetched = new Map<Kind, unknown>(response)
  const currentStore = useRenewStore.getState()
  const next: Omit<StudioData, 'quest'> = {
    worlds: currentStore.worlds,
    characters: currentStore.characters,
    statuses: currentStore.statuses,
    commands: currentStore.commands,
    chats: currentStore.chats,
  }
  for (const kind of collections) {
    const value = fetched.get(kind)
    if (!value) continue
    const rows = normalizeStudioCollection(
      kind,
      value as StudioCollectionResponse
    )
    for (const id of Object.keys(rows)) local(kind, id)
    Object.assign(next, { [COLLECTION[kind]]: rows })
  }
  const settle: [Kind, string][] = []
  for (const kind of collections) {
    const collection = COLLECTION[kind]
    for (const [key, version] of adoptedVersions) {
      if (
        !key.startsWith(`${kind}:`) ||
        version === (readVersions.get(key) ?? 0)
      )
        continue
      const id = key.slice(key.indexOf(':') + 1)
      const item = currentStore[collection][id]
      if (item) Object.assign(next[collection], { [id]: item })
      else delete (next[collection] as Record<string, unknown>)[id]
    }
  }
  // 현재 탭에서 저장 중인 편집만 서버 목록 위에 보존한다.
  for (const [key, mark] of Object.entries(pending)) {
    if (key === keyOf('chat', WORK_DRAFT_ID) && mark.soft) {
      const memoryDraft = currentStore.chats[WORK_DRAFT_ID]
      const draft = memoryDraft
      if (mark.serverId) {
        const saved =
          next.chats[mark.serverId] ?? currentStore.chats[mark.serverId]
        if (saved) {
          next.chats[mark.serverId] = draft
            ? {
                ...saved,
                ...draft,
                id: mark.serverId,
                review: saved.review,
                audienceLocked: saved.audienceLocked,
              }
            : saved
          if (draft) {
            const savedKey = keyOf('chat', mark.serverId)
            pending[savedKey] = {}
            entries.set(savedKey, { dirty: true, needed: false, failed: true })
          }
        }
        delete next.chats[WORK_DRAFT_ID]
        useWorkDraftStatus.setState({
          owner: nextOwner,
          serverId: mark.serverId,
          intent: mark.intent ?? null,
        })
      } else if (draft) {
        next.chats[WORK_DRAFT_ID] = draft
        if (!entries.get(key)?.flight && !entries.get(key)?.timer) {
          const uncertain = !!(mark.creating || mark.uncertain)
          pending[key] = workDraftMark(uncertain ? { uncertain: true } : {})
          entries.set(key, {
            dirty: hasWorkDraftInput(draft),
            needed: false,
            failed: hasWorkDraftInput(draft),
            uncertain,
          })
        }
      }
      continue
    }
    const materialSeparator = key.indexOf(':')
    const materialKind = key.slice(0, materialSeparator) as Kind
    const materialId = key.slice(materialSeparator + 1)
    if (
      materialKind !== 'chat' &&
      KINDS.includes(materialKind) &&
      isMaterialDraftId(materialId) &&
      mark.soft
    ) {
      const target = next[COLLECTION[materialKind]] as Record<string, unknown>
      if (mark.serverId) {
        delete target[materialId]
        continue
      }
      const localDraft = currentStore[COLLECTION[materialKind]][materialId]
      if (localDraft) target[materialId] = localDraft
      if (entries.get(key)?.flight) continue
      if (mark.creating || mark.uncertain) {
        pending[key] = { ...mark, creating: false, uncertain: true }
        const e = entry(materialKind, materialId)
        e.uncertain = true
        e.failed = true
      }
      continue
    }
    if (mark.creating) {
      pending[key] = { uncertain: true }
      mark.uncertain = true
    }
    const separator = key.indexOf(':')
    const kind = key.slice(0, separator) as Kind
    const id = key.slice(separator + 1)
    if (separator < 0 || !KINDS.includes(kind) || !id) continue
    const target = next[COLLECTION[kind]] as Record<string, unknown>
    const wasFetched = fetched.has(kind)
    if (mark.deleted && wasFetched && !target[id]) {
      delete pending[key]
      delete ids[key]
      continue
    }
    const remote = wasFetched ? target[id] : undefined
    const localCopy = currentStore[COLLECTION[kind]][id]
    const reconciled =
      wasFetched && !!mark.uncertain && !!ids[key] && !!target[id]
    if (reconciled) pending[key] = {}
    if (mark.deleted) delete target[id]
    else if (localCopy) target[id] = localCopy
    else {
      delete pending[key]
      continue
    }
    const localRevision = (localCopy as { revision?: string } | undefined)
      ?.revision
    // 예전 미저장 초안에는 기준 본문이 없다. 버전이 다르면 재전송 대신 비교한다.
    // 내 저장이 전송 중이거나, 목록을 읽기 시작한 뒤 내 저장이 끝났으면 내가 올린 버전이다. 충돌로 보지 않는다.
    const staleRead =
      (adoptedVersions.get(key) ?? 0) !== (readVersions.get(key) ?? 0)
    const conflict =
      !!mark.conflict ||
      (!staleRead &&
        !entries.get(key)?.flight &&
        !!remote &&
        !!localRevision &&
        (remote as { revision?: string }).revision !== localRevision)
    if (conflict) pending[key] = { ...pending[key], conflict: true }
    if (!entries.has(key))
      entries.set(key, {
        dirty: !!reconciled || (!mark.uncertain && !mark.deleted),
        needed: false,
        failed: true,
        uncertain: mark.uncertain && !reconciled,
        conflict,
      })
    else if (conflict) {
      const e = entries.get(key)!
      e.conflict = true
      e.failed = true
      clearTimeout(e.timer)
      e.timer = undefined
    }
    if (conflict) settle.push([kind, id])
  }
  silently(() => useRenewStore.getState().hydrate(next))
  start()
  for (const [kind, id] of settle) void settleLoadedConflict(kind, id)
  for (const kind of collections)
    useStudioCollectionStatus.setState((state) => ({
      byKind: { ...state.byKind, [kind]: 'ready' },
      error: { ...state.error, [kind]: undefined },
    }))
  failedKinds = failedKinds.filter((kind) => !collections.includes(kind))
  loadingFailed = failedKinds.length > 0
  // 입력 뒤 저장을 기다리는 편집은 정상 상태다. 실패 · 결과 불명만 알린다(충돌은 판정이 끝난 뒤 settleLoadedConflict 가 알린다)
  const settling = new Set(settle.map(([kind, id]) => keyOf(kind, id)))
  const unconfirmed = Object.entries(pending).some(([key, mark]) => {
    if (settling.has(key)) return false
    if (mark.soft && entries.get(key)?.flight) return false
    if (mark.soft && isMaterialDraftId(key.slice(key.indexOf(':') + 1)))
      return !mark.serverId && (!!mark.uncertain || !!entries.get(key)?.failed)
    return !!mark.uncertain || !!entries.get(key)?.failed
  })
  const unsaved = Object.keys(pending).flatMap((key) => {
    const separator = key.indexOf(':')
    const kind = key.slice(0, separator) as Kind
    const id = key.slice(separator + 1)
    return KINDS.includes(kind) && entity(kind, id) ? [labelOf(kind, id)] : []
  })
  const names =
    unsaved.length > 2
      ? `${unsaved.slice(0, 2).join(', ')} 외 ${unsaved.length - 2}개`
      : unsaved.join(', ')
  if (unconfirmed)
    useSyncStatus.setState({
      savedAt: null,
      error: [...entries.values()].some((e) => e.conflict)
        ? '서버 저장본과 다른 편집이 있어요. 표시된 항목에서 비교하고 저장해 주세요.'
        : Object.values(pending).some((mark) => mark.uncertain)
          ? `${names || '새 항목'} 생성 요청의 결과를 확인하지 못했어요. 서버 목록을 다시 읽었고, 중복 생성을 막으려고 자동으로 다시 보내지 않아요.`
          : `${names || '편집'}의 현재 탭 편집이 아직 저장되지 않았어요. ‘다시 시도’를 눌러 주세요.`,
    })
  else refreshError()
}

export async function ensureStudioCollections(collections: readonly Kind[]) {
  const nextOwner = useRenewStore.getState().owner
  if (!nextOwner) return
  await Promise.all(
    collections.map(async (kind) => {
      if (
        owner === nextOwner &&
        useStudioCollectionStatus.getState().byKind[kind] === 'ready'
      )
        return
      const key = `${nextOwner}:${kind}`
      let flight = collectionFlights.get(key)
      if (!flight) {
        flight = Promise.resolve().then(async () => {
          if (useRenewStore.getState().owner !== nextOwner) return
          return loadStudio(nextOwner, [kind])
        })
        collectionFlights.set(key, flight)
        void flight.finally(() => collectionFlights.delete(key)).catch(() => {})
      }
      await flight
    })
  )
}

/** 편집 대상과 현재 작품이 실제로 가리키는 재료만 읽는다. */
export function studioEditorReady(kind: Kind, id: string, step: string) {
  const state = useRenewStore.getState()
  const ready = useStudioCollectionStatus.getState().byKind
  const enteredHere =
    isLocalMaterialDraft(kind, id) ||
    (kind !== 'chat' &&
      Object.entries(pending).some(
        ([key, mark]) =>
          key.startsWith(`${kind}:draft-`) && mark.soft && mark.serverId === id
      ))
  if (state.owner && owner === state.owner && enteredHere && entity(kind, id))
    return true
  if (!state.owner || owner !== state.owner || ready[kind] !== 'ready')
    return false
  const chat = kind === 'chat' ? state.chats[id] : undefined
  if (chat) {
    if (
      (chat.characterIds.length || step === 'cast' || step === 'starts') &&
      ready.character !== 'ready'
    )
      return false
    if (
      (chat.worldId ||
        chat.characterIds.some(
          (characterId) => !!state.characters[characterId]?.worldId
        ) ||
        step === 'album') &&
      ready.world !== 'ready'
    )
      return false
    if (
      (chat.statusId || step === 'status' || step === 'tools') &&
      ready.status !== 'ready'
    )
      return false
    if (
      (chat.commandIds.length || step === 'commands' || step === 'tools') &&
      ready.command !== 'ready'
    )
      return false
  } else if (
    kind === 'character' &&
    (state.characters[id]?.worldId || step === 'world') &&
    ready.world !== 'ready'
  )
    return false
  else if ((kind === 'status' || kind === 'command') && ready.chat !== 'ready')
    return false
  return true
}

export async function ensureStudioEditor(kind: Kind, id: string, step: string) {
  const editorOwner = useRenewStore.getState().owner
  const active = () =>
    !!editorOwner && useRenewStore.getState().owner === editorOwner
  if (!active()) return
  await ensureStudioCollections([kind])
  if (!active()) return
  if (kind !== 'chat' && isMaterialDraftId(id)) {
    const requestedId = id
    const cachedId = readMaterialDraftId(editorOwner!, kind, requestedId)
    if (
      cachedId &&
      entity(kind, cachedId) &&
      serverId(kind, cachedId) === cachedId
    )
      ids[keyOf(kind, requestedId)] = cachedId
    id = studioServerId(kind, requestedId)
    if (id === requestedId && !entity(kind, id))
      beginMaterialDraft(kind, {}, id)
  }
  if (kind === 'chat' && id === WORK_DRAFT_ID) {
    if (
      useWorkDraftStatus.getState().owner === editorOwner &&
      useWorkDraftStatus.getState().serverId
    )
      return
    if (!useRenewStore.getState().chats[id]) beginWorkDraft()
  }
  if (kind === 'chat') {
    const chat = useRenewStore.getState().chats[id]
    if (!chat) return
    if (chat.characterIds.length || step === 'cast' || step === 'starts')
      await ensureStudioCollections(['character'])
    if (!active()) return
    const characters = useRenewStore.getState().characters
    if (
      chat.worldId ||
      chat.characterIds.some(
        (characterId) => !!characters[characterId]?.worldId
      ) ||
      step === 'album'
    )
      await ensureStudioCollections(['world'])
    if (!active()) return
    if (chat.statusId || step === 'status' || step === 'tools')
      await ensureStudioCollections(['status'])
    if (!active()) return
    if (chat.commandIds.length || step === 'commands' || step === 'tools')
      await ensureStudioCollections(['command'])
  } else if (
    kind === 'character' &&
    (useRenewStore.getState().characters[id]?.worldId || step === 'world')
  )
    await ensureStudioCollections(['world'])
  else if (kind === 'status' || kind === 'command')
    await ensureStudioCollections(['chat'])
  if (!active()) return
  if (
    (kind === 'chat' &&
      (step === 'commands' ||
        step === 'tools' ||
        !!useRenewStore.getState().chats[id]?.officialCommands.length)) ||
    kind === 'command'
  )
    void ensureStudioSupport('official')
  if (!active()) return
  if (
    (kind === 'chat' &&
      (step === 'intro' ||
        step === 'cast' ||
        step === 'publish' ||
        step === 'album')) ||
    (kind === 'character' && (step === 'profile' || step === 'scenes')) ||
    (kind === 'world' && step === 'basic')
  )
    void ensureStudioSupport('media')
}

async function ensureStudioSupport(kind: 'official' | 'media') {
  const nextOwner = useRenewStore.getState().owner
  if (!nextOwner) return
  const key = `${nextOwner}:${kind}`
  if (supportReady.has(key)) return
  let flight = supportFlights.get(key)
  if (!flight) {
    flight = Promise.resolve().then(async () => {
      if (useRenewStore.getState().owner !== nextOwner) return
      try {
        if (kind === 'official') {
          const response = z
            .object({ commands: z.array(z.object({ key: z.string() })) })
            .parse(await api<unknown>('/studio/command/official/'))
          if (useRenewStore.getState().owner === nextOwner)
            officialKeys = new Set(
              normalizeOfficialCommands(
                response.commands.map((command) => command.key)
              )
            )
        } else {
          const response = await api<{ assets: MediaAsset[] }>(
            '/creator/me/media/'
          )
          if (useRenewStore.getState().owner === nextOwner)
            rememberAssets(response.assets)
        }
        if (useRenewStore.getState().owner === nextOwner) supportReady.add(key)
      } catch {
        // 보조 목록 실패는 편집을 막지 않는다. 다음 진입에서 다시 시도한다.
      }
    })
    supportFlights.set(key, flight)
    void flight.finally(() => supportFlights.delete(key)).catch(() => {})
  }
  await flight
}

/* ---------- 저장 ---------- */

function materialHasInput(kind: MaterialKind, item: unknown) {
  return !!item && !isBlankDraft(kind, item)
}

/** 최초 생성 전송 중의 입력을 보존하고 연결된 로컬 참조도 실제 ID로 한 번에 바꾼다. */
export function acceptRecoveredMaterialDraft(
  kind: MaterialKind,
  localId: string,
  id: string,
  sentBody: Record<string, unknown>
) {
  const context = contextOf()
  if (!context || !current(context) || !isLocalMaterialDraft(kind, localId))
    return
  const state = useRenewStore.getState()
  const local = entity(kind, localId)
  const saved = entity(kind, id)
  if (!local || !saved)
    throw new Error('생성된 항목과 작성 중인 내용을 다시 확인해 주세요.')
  const serverBody = bodyOf(kind, saved, 'server')
  let item: unknown
  let changed = true
  let conflict = false
  try {
    const localBody = bodyOf(kind, local)
    const comparison = compareSaveBodies(sentBody, localBody, serverBody)
    const merged = { ...comparison.merged }
    for (const field of comparison.conflicts) merged[field] = localBody[field]
    changed = !sameSaveValue(merged, serverBody)
    conflict = comparison.conflicts.length > 0
    item = {
      ...(projectCreated(kind, {
        ...merged,
        id,
        revision: (saved as { revision?: string }).revision,
      }).item as object),
      updatedAt: (local as { updatedAt: number }).updatedAt,
    }
  } catch {
    // 생성 중 잠시 유효하지 않게 바뀐 입력도 버리지 않고 실제 ID에서 계속 편집한다.
    item = {
      ...(saved as object),
      ...(local as object),
      id,
      revision: (saved as { revision?: string }).revision,
    }
  }
  const localKey = keyOf(kind, localId)
  ids[localKey] = id
  ids[keyOf(kind, id)] = id
  pending[localKey] = {
    ...pending[localKey],
    soft: true,
    serverId: id,
    creating: false,
    uncertain: false,
  }
  rememberMaterialDraftId(context.owner, kind, localId, id)
  const previous = entries.get(localKey)
  clearTimeout(previous?.timer)
  if (previous) previous.dirty = false
  entries.delete(localKey)
  clearSaveIssue(kind, localId)

  const collection = COLLECTION[kind]
  const materialItems = { ...state[collection], [id]: item }
  delete materialItems[localId]
  const next = {
    worlds: state.worlds,
    characters: state.characters,
    statuses: state.statuses,
    commands: state.commands,
    chats: state.chats,
    [collection]: materialItems,
  } as Omit<StudioData, 'quest'>
  const touched: [Kind, string][] = []
  if (kind === 'world') {
    next.characters = Object.fromEntries(
      Object.entries(next.characters).map(([key, character]) => {
        if (character.worldId !== localId) return [key, character]
        touched.push(['character', key])
        return [key, { ...character, worldId: id }]
      })
    )
  }
  next.chats = Object.fromEntries(
    Object.entries(next.chats).map(([key, work]) => {
      const linked =
        kind === 'character'
          ? work.characterIds.includes(localId) ||
            work.lead === localId ||
            work.starts.some((start) =>
              start.intro.lines.some((line) => line.charId === localId)
            )
          : kind === 'world'
            ? work.worldId === localId
            : kind === 'status'
              ? work.statusId === localId
              : work.commandIds.includes(localId)
      if (!linked) return [key, work]
      touched.push(['chat', key])
      return [
        key,
        {
          ...work,
          ...(kind === 'character'
            ? {
                characterIds: [
                  ...new Set(
                    work.characterIds.map((value) =>
                      value === localId ? id : value
                    )
                  ),
                ],
                lead: work.lead === localId ? id : work.lead,
                starts: work.starts.map((start) => ({
                  ...start,
                  intro: {
                    ...start.intro,
                    lines: start.intro.lines.map((line) =>
                      line.charId === localId ? { ...line, charId: id } : line
                    ),
                  },
                })),
              }
            : {}),
          ...(kind === 'world' ? { worldId: id } : {}),
          ...(kind === 'status' ? { statusId: id } : {}),
          ...(kind === 'command'
            ? {
                commandIds: [
                  ...new Set(
                    work.commandIds.map((value) =>
                      value === localId ? id : value
                    )
                  ),
                ],
              }
            : {}),
        },
      ]
    })
  )
  if (changed) {
    const key = keyOf(kind, id)
    pending[key] = { base: serverBody, ...(conflict ? { conflict: true } : {}) }
    entries.set(key, { dirty: true, needed: false, failed: conflict, conflict })
  }
  for (const [refKind, refId] of touched) {
    const key = keyOf(refKind, refId)
    pending[key] ??=
      refKind === 'chat' && refId === WORK_DRAFT_ID ? workDraftMark() : {}
    const e = entry(refKind, refId)
    e.dirty = true
    if (!e.conflict && !e.uncertain) e.failed = false
  }
  silently(() => state.hydrate(next))
  markAdopted(keyOf(kind, id))
  if (conflict)
    setSaveIssue(
      kind,
      id,
      '생성 중 변경된 내용과 최신 저장본을 비교해 주세요.',
      true
    )
  else if (changed) schedule(kind, id)
  for (const [refKind, refId] of touched)
    if (!entry(refKind, refId).flight) schedule(refKind, refId)
  if (!hasPendingWrites()) useSyncStatus.setState({ savedAt: Date.now() })
  refreshError()
}

async function saveMaterialDraft(
  kind: MaterialKind,
  localId: string,
  e: Entry,
  context: Context
) {
  const key = keyOf(kind, localId)
  const item = entity(kind, localId)
  if (!item || !materialHasInput(kind, item)) {
    e.dirty = false
    return
  }
  if (pending[key]?.uncertain || pending[key]?.creating) {
    e.failed = true
    e.uncertain = true
    return
  }
  e.dirty = false
  e.failed = false
  let attempted = false
  useSyncStatus.setState((status) => ({ saving: status.saving + 1 }))
  try {
    for (const [refKind, refId] of refsOf(kind, item)) {
      if (serverId(refKind, refId)) continue
      const ref = entity(refKind, refId)
      if (
        isLocalMaterialDraft(refKind, refId) &&
        (!ref || !materialHasInput(refKind as MaterialKind, ref))
      )
        return
      await flush(refKind, refId)
      if (!current(context)) return
      if (!serverId(refKind, refId))
        throw new Error('연결할 항목을 먼저 저장해 주세요.')
    }
    for (let pass = 0; pass < 3; pass += 1) {
      await uploadImages(kind, localId, context)
      if (!current(context)) return
      const latest = entity(kind, localId)
      const sources =
        kind === 'world'
          ? [(latest as World | undefined)?.cover]
          : kind === 'character'
            ? [
                (latest as Character | undefined)?.image,
                (latest as Character | undefined)?.avatar,
                (latest as Character | undefined)?.imageCrop?.src,
                (latest as Character | undefined)?.avatarCrop?.src,
                ...((latest as Character | undefined)?.album ?? []).map(
                  (shot) => shot.image
                ),
              ]
            : []
      if (!sources.some((source) => isDataUrl(source))) break
      if (pass === 2)
        throw new Error('이미지 처리가 끝난 뒤 저장을 다시 시도해 주세요.')
    }
    const latest = entity(kind, localId)
    if (!latest || !materialHasInput(kind, latest)) return
    const body = bodyOf(kind, latest)
    pending[key] = { ...pending[key], soft: true, creating: true }
    const { persistMaterialDraft } =
      await import('@/features/studio-renew/services/studio-crud.service')
    if (!current(context)) return
    const beforePost = entity(kind, localId)
    if (!beforePost || !materialHasInput(kind, beforePost)) {
      pending[key] = { ...pending[key], creating: false }
      return
    }
    attempted = true
    await persistMaterialDraft(kind, body, localId)
    if (current(context)) e.dirty = false
  } catch (error) {
    if (!current(context)) return
    const rejected =
      error instanceof ApiError &&
      !error.unknownOutcome &&
      error.status >= 400 &&
      error.status < 500 &&
      (error.status !== 409 || error.code === 'CREATOR_CREATE_NOT_SENT')
    const uncertain = attempted && !rejected && !pending[key]?.serverId
    pending[key] = { ...pending[key], soft: true, creating: false, uncertain }
    e.failed = true
    e.uncertain = uncertain
    setSaveIssue(kind, localId, messageOf(error))
    refreshError(messageOf(error))
  } finally {
    if (current(context))
      useSyncStatus.setState((status) => ({
        saving: Math.max(0, status.saving - 1),
      }))
  }
}

function promoteWorkDraft(id: string, context: Context, sentBody?: string) {
  if (!current(context)) return
  const state = useRenewStore.getState()
  const draft = state.chats[WORK_DRAFT_ID]
  const saved = state.chats[id]
  if (!saved) throw new Error('생성된 작품을 목록에서 다시 확인해 주세요.')
  let local: SaveBody | undefined
  let base: SaveBody | undefined
  if (draft) local = bodyOf('chat', draft)
  if (sentBody) {
    try {
      const parsed: unknown = JSON.parse(sentBody)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed))
        base = parsed as SaveBody
    } catch {
      /* 기준 본문을 잃었으면 겹치는 변경은 직접 고른다. */
    }
  }
  const server = bodyOf('chat', saved, 'server')
  const comparison = local
    ? compareSaveBodies(base, local, server)
    : { merged: server, conflicts: [] as string[] }
  const merged = { ...comparison.merged }
  for (const field of comparison.conflicts) merged[field] = local?.[field]
  const changed = !sameSaveValue(merged, server)
  const mergedItem = changed
    ? chatIn(
        {
          id,
          work: merged,
          version: 0,
          published: false,
          reviewStatus: saved.review,
          revision: saved.revision,
          internalReview: saved.internalReview,
          publication: saved.publication,
          audienceLocked: saved.audienceLocked,
        },
        (_kind, refId) => refId
      )
    : saved
  const nextItem = changed
    ? {
        ...mergedItem,
        revision: saved.revision,
        internalReview: saved.internalReview,
        publication: saved.publication,
        review: saved.review,
        rejectReason: saved.rejectReason,
        submittedAt: saved.submittedAt,
        audienceLocked: saved.audienceLocked,
        updatedAt: saved.updatedAt,
      }
    : saved
  const chats = { ...state.chats, [id]: nextItem }
  delete chats[WORK_DRAFT_ID]
  silently(() =>
    state.hydrate({
      worlds: state.worlds,
      characters: state.characters,
      statuses: state.statuses,
      commands: state.commands,
      chats,
    })
  )
  const draftKey = keyOf('chat', WORK_DRAFT_ID)
  pending[draftKey] = workDraftMark({ serverId: id })
  entries.delete(draftKey)
  useWorkDraftStatus.setState({
    owner: context.owner,
    serverId: id,
    intent: pending[draftKey].intent ?? null,
  })
  if (changed) {
    const key = keyOf('chat', id)
    const conflict = comparison.conflicts.length > 0
    pending[key] = {
      base: conflict ? base : server,
      ...(conflict ? { conflict: true } : {}),
    }
    const e = entry('chat', id)
    e.dirty = true
    e.failed = conflict
    e.conflict = conflict
    if (conflict)
      setSaveIssue(
        'chat',
        id,
        '다른 창에서 같은 작품 항목을 수정했어요. 저장할 내용을 비교해 주세요.',
        true
      )
    else schedule('chat', id)
  } else if (!hasPendingWrites())
    useSyncStatus.setState({ savedAt: Date.now() })
  refreshError()
}

/** 생성 결과 재확인 뒤 예약 초안을 실제 서버 ID로 옮기고, 전송 중 입력은 이어서 저장한다. */
export function acceptRecoveredWorkDraft(id: string, sentBody: string) {
  const context = contextOf()
  if (!context || !current(context))
    throw new Error('계정이 바뀌어 작품 초안의 생성 결과를 적용하지 않았어요.')
  promoteWorkDraft(id, context, sentBody)
}

/** 유효 값이 입력되기 전에는 POST하지 않고, 응답 대기 중의 입력도 실제 ID 쪽에 보존한다. */
async function saveWorkDraft(e: Entry, context: Context) {
  const key = keyOf('chat', WORK_DRAFT_ID)
  if (pending[key]?.serverId) {
    promoteWorkDraft(pending[key].serverId!, context)
    return
  }
  const initial = useRenewStore.getState().chats[WORK_DRAFT_ID]
  if (!initial || !hasWorkDraftInput(initial)) {
    e.dirty = false
    return
  }
  if (pending[key]?.creating || pending[key]?.uncertain) {
    e.failed = true
    e.uncertain = true
    refreshError('이전 작품의 생성 결과를 먼저 확인해 주세요.')
    return
  }
  e.dirty = false
  e.failed = false
  refreshError()
  let attempted = false
  useSyncStatus.setState((status) => ({ saving: status.saving + 1 }))
  try {
    const { persistWorkDraft } =
      await import('@/features/studio-renew/services/studio-crud.service')
    if (!current(context)) return
    for (const [kind, id] of refsOf('chat', initial)) {
      if (serverId(kind, id)) continue
      if (!entity(kind, id))
        throw new Error('작품에 연결할 재료를 먼저 확인해 주세요.')
      if (
        isLocalMaterialDraft(kind, id) &&
        !materialHasInput(kind as MaterialKind, entity(kind, id))
      )
        return
      await flush(kind, id)
      if (!current(context)) return
      if (!serverId(kind, id))
        throw new Error('연결할 재료가 서버에 저장되지 않았어요.')
    }
    for (let pass = 0; pass < 3; pass += 1) {
      await uploadImages('chat', WORK_DRAFT_ID, context)
      if (!current(context)) return
      const draft = useRenewStore.getState().chats[WORK_DRAFT_ID]
      if (!draft) return
      const media = [
        draft.cover,
        ...(draft.covers ?? []),
        draft.introGif,
        draft.titleImage,
        ...draft.albumPlaces.flatMap((place) =>
          place.scenes.map((scene) => scene.image)
        ),
        ...draft.starts.flatMap((start) =>
          start.intro.lines.map((line) => line.image)
        ),
      ]
      if (!media.some((src) => typeof src === 'string' && isDataUrl(src))) break
      if (pass === 2)
        throw new Error('이미지 처리가 끝난 뒤 작품 저장을 다시 시도해 주세요.')
    }
    const latest = useRenewStore.getState().chats[WORK_DRAFT_ID]
    if (!latest || !hasWorkDraftInput(latest)) {
      e.dirty = false
      return
    }
    const payload = bodyOf('chat', latest)
    pending[key] = workDraftMark({ creating: true })
    if (!current(context)) return
    const beforePost = useRenewStore.getState().chats[WORK_DRAFT_ID]
    if (!beforePost || !hasWorkDraftInput(beforePost)) {
      pending[key] = workDraftMark()
      e.dirty = false
      return
    }
    attempted = true
    const id = await persistWorkDraft(payload)
    if (!current(context)) return
    pending[key] = workDraftMark({ serverId: id })
    e.dirty = false
    promoteWorkDraft(id, context, JSON.stringify(payload))
  } catch (error) {
    if (!current(context)) return
    const rejected =
      error instanceof ApiError &&
      !error.unknownOutcome &&
      error.status >= 400 &&
      error.status < 500 &&
      error.status !== 409
    const uncertain = attempted && !rejected && !pending[key]?.serverId
    pending[key] = pending[key]?.serverId
      ? workDraftMark({ serverId: pending[key].serverId })
      : workDraftMark(uncertain ? { uncertain: true } : {})
    e.failed = true
    e.uncertain = uncertain
    refreshError(messageOf(error))
  } finally {
    if (current(context))
      useSyncStatus.setState((status) => ({
        saving: Math.max(0, status.saving - 1),
      }))
  }
}

function schedule(kind: Kind, id: string) {
  const e = entry(kind, id)
  if (e.uncertain || e.conflict) return
  clearTimeout(e.timer)
  e.timer = setTimeout(() => void flush(kind, id), DEBOUNCE_MS)
}

/** 지금 바로 저장 — 이미 보내는 중이면 그 요청을 기다린다(끝난 뒤 또 바뀌었으면 다시 잡힌다) */
function flush(kind: Kind, id: string): Promise<void> {
  const context = contextOf()
  if (!context) return Promise.resolve()
  const e = entry(kind, id)
  clearTimeout(e.timer)
  e.timer = undefined
  if (e.flight) return e.flight
  const flight = save(kind, id, e, context).finally(() => {
    if (!current(context)) return
    e.flight = undefined
    // 보내는 사이에 지웠으면 방금 만든 서버 것도 지운다
    if (
      !entity(kind, id) &&
      !(kind === 'chat' && id === WORK_DRAFT_ID) &&
      !isLocalMaterialDraft(kind, id)
    )
      void removeRemote(kind, id, context)
    else if (e.dirty && !e.failed) schedule(kind, id)
  })
  e.flight = flight
  return flight
}

async function save(kind: Kind, id: string, e: Entry, context: Context) {
  if (!current(context) || e.uncertain || e.conflict) return
  const item = entity(kind, id)
  if (!item) return
  if (kind !== 'chat' && isLocalMaterialDraft(kind, id)) {
    try {
      await saveMaterialDraft(kind, id, e, context)
    } catch (error) {
      if (current(context)) {
        e.failed = true
        refreshError(messageOf(error))
      }
    }
    return
  }
  if (kind === 'chat' && id === WORK_DRAFT_ID) {
    try {
      await saveWorkDraft(e, context)
    } catch (error) {
      if (current(context)) {
        e.failed = true
        refreshError(messageOf(error))
      }
    }
    return
  }
  const sid = serverId(kind, id)
  if (!sid && !e.needed && isBlankDraft(kind, item)) {
    e.dirty = false
    return
  }
  if (!sid || sid !== id) {
    e.dirty = false
    e.failed = true
    useSyncStatus.setState({
      savedAt: null,
      error:
        '서버 ID가 없는 초안은 자동 저장하지 않아요. 서버에서 새 항목을 만든 뒤 편집해 주세요.',
    })
    return
  }
  // 고정 검토본이 없는 구 서버의 pending은 보호한다. 새 검토본은 초안과 분리된다.
  if (kind === 'chat' && sid && isStudioWorkLocked(item as Chat)) {
    e.dirty = false
    return
  }
  e.dirty = false
  e.failed = false
  useSyncStatus.setState((s) => ({ saving: s.saving + 1 }))
  try {
    for (const [refKind, refId] of refsOf(kind, item)) {
      // 최신 서버 저장본에서 확인한 참조는 이 탭에 재료 본문이 없어도 유효하다.
      if (serverId(refKind, refId)) continue
      if (
        isLocalMaterialDraft(refKind, refId) &&
        !materialHasInput(refKind as MaterialKind, entity(refKind, refId))
      )
        return
      if (!entity(refKind, refId))
        throw new Error('작품에 연결한 재료가 없어요. 연결을 확인해 주세요.')
      entry(refKind, refId).needed = true
      await flush(refKind, refId)
      if (!current(context)) return
      // 앞선 요청이 ‘빈 초안이라 건너뜀’으로 끝났으면 이번엔 만든다
      if (!serverId(refKind, refId)) await flush(refKind, refId)
      if (!current(context)) return
      if (!serverId(refKind, refId))
        throw new Error('연결한 재료를 먼저 저장하지 못했어요.')
    }
    await uploadImages(kind, id, context)
    if (!current(context)) return
    const latest = entity(kind, id)
    if (!latest) return
    const body = bodyOf(kind, latest)
    if (!current(context)) return
    const revision = (latest as { revision?: string }).revision
    const result = await api<{
      id: string
      revision?: string
      work?: Record<string, unknown>
      internalReview?: unknown
      publication?: unknown
      audienceLocked?: boolean
    }>(
      `${PATH[kind]}${sid}/`,
      {
        ...(kind === 'chat' ? { work: body, schema: 'studio-renew' } : body),
        ...(revision ? { baseRevision: revision } : {}),
      },
      'PATCH'
    )
    if (!result || result.id !== sid)
      throw new Error(
        '서버 저장 응답의 작품 ID가 다릅니다. 저장 상태를 확인해 주세요.'
      )
    if (!current(context)) return
    if (kind === 'chat' && 'introGif' in body) {
      const saved = result.work
        ? result
        : await api<ServerWork>(`${PATH.chat}${sid}/`)
      if (!current(context)) return
      if (saved.id !== sid || (saved.work?.introGif ?? '') !== body.introGif)
        throw new Error(
          '탐색 표지가 서버에 저장되지 않았어요. 편집 내용은 보관했으니 저장 상태를 확인해 주세요.'
        )
    }
    // 저장 응답의 메타데이터만 갱신하고 전송 중 입력은 보존한다.
    const currentItem = entity(kind, id)
    if (currentItem) {
      const metadata: Record<string, unknown> = {
        ...(result.revision ? { revision: result.revision } : {}),
      }
      if (kind === 'chat') {
        const review = internalReviewSchema.safeParse(result.internalReview)
        const publication = publicationSchema.safeParse(result.publication)
        if (review.success) metadata.internalReview = review.data
        if (publication.success) metadata.publication = publication.data
        if (typeof result.audienceLocked === 'boolean')
          metadata.audienceLocked = result.audienceLocked
      }
      if (Object.keys(metadata).length)
        silently(() => {
          const state = useRenewStore.getState()
          const collection = COLLECTION[kind]
          state.hydrate({
            worlds: state.worlds,
            characters: state.characters,
            statuses: state.statuses,
            commands: state.commands,
            chats: state.chats,
            [collection]: {
              ...state[collection],
              [id]: { ...(currentItem as object), ...metadata },
            },
          })
        })
    }
    markAdopted(keyOf(kind, id))
    clearSaveIssue(kind, id)
    if (e.dirty)
      pending[keyOf(kind, id)] = { ...pending[keyOf(kind, id)], base: body }
    if (!e.dirty && entity(kind, id)) {
      delete pending[keyOf(kind, id)]
    }
    if (!hasPendingWrites()) useSyncStatus.setState({ savedAt: Date.now() })
    refreshError()
  } catch (error) {
    if (!current(context)) return
    e.failed = true
    const message = saveFailure(kind, id, error)
    if (error instanceof ApiError && error.code === 'SAVE_CONFLICT') {
      e.conflict = true
      pending[keyOf(kind, id)] = { ...pending[keyOf(kind, id)], conflict: true }
      settlingConflicts.add(keyOf(kind, id))
      try {
        const comparison = withLatestEdit(await inspectSaveConflict(kind, id))
        if (!comparison.conflicts.length) await adoptConflict(comparison, {})
      } catch {
        /* 서버 조회가 실패해도 초안과 충돌 상태를 유지한다. 비교 버튼에서 다시 읽는다. */
      }
      settlingConflicts.delete(keyOf(kind, id))
      if (!current(context)) return
      if (e.conflict) setSaveIssue(kind, id, message, true)
    } else setSaveIssue(kind, id, message)
    refreshError(message)
  } finally {
    if (current(context))
      useSyncStatus.setState((s) => ({ saving: Math.max(0, s.saving - 1) }))
  }
}

/** 서버에서 지우기 — 재료는 DELETE(서버가 작품 연결도 푼다), 작품은 보관(archive, 서버에 작품 DELETE 가 없다 · 5-2) */
async function removeRemote(kind: Kind, id: string, context = contextOf()) {
  if (!context || !current(context)) return
  const sid = serverId(kind, id)
  entries.delete(keyOf(kind, id))
  if (!sid || sid !== id) {
    delete pending[keyOf(kind, id)]
    return
  }
  useSyncStatus.setState((s) => ({ saving: s.saving + 1 }))
  try {
    if (kind === 'chat')
      await api(`${PATH.chat}${sid}/archive/`, { reason: '작가 삭제' })
    else await api(`${PATH[kind]}${sid}/`, undefined, 'DELETE')
    if (!current(context)) return
    markAdopted(keyOf(kind, id))
    delete ids[keyOf(kind, id)]
    delete pending[keyOf(kind, id)]
    if (!hasPendingWrites()) useSyncStatus.setState({ savedAt: Date.now() })
  } catch (error) {
    if (current(context)) {
      entries.set(keyOf(kind, id), {
        dirty: false,
        needed: false,
        failed: true,
      })
      useSyncStatus.setState({ error: messageOf(error), savedAt: null })
    }
  } finally {
    if (current(context))
      useSyncStatus.setState((s) => ({ saving: Math.max(0, s.saving - 1) }))
  }
}

/** store 변화 → 바뀐 엔티티는 3초 뒤 저장, 사라진 엔티티는 서버에서도 지운다 */
function start() {
  if (unsubscribe) return
  unsubscribe = useRenewStore.subscribe((nextState, prevState) => {
    if (
      applying ||
      !owner ||
      nextState.owner !== owner ||
      prevState.owner !== owner
    )
      return
    for (const kind of KINDS) {
      const before = prevState[COLLECTION[kind]] as Record<string, unknown>
      const after = nextState[COLLECTION[kind]] as Record<string, unknown>
      if (before === after) continue
      for (const id of Object.keys(after)) {
        if (after[id] === before[id]) continue
        if (
          isLocalMaterialDraft(kind, id) &&
          !entries.get(keyOf(kind, id))?.flight &&
          !pending[keyOf(kind, id)]?.uncertain &&
          isBlankDraft(kind, after[id])
        ) {
          const e = entry(kind, id)
          clearTimeout(e.timer)
          e.timer = undefined
          e.dirty = false
          e.failed = false
          refreshError()
          continue
        }
        if (kind === 'chat' && id === WORK_DRAFT_ID) {
          pending[keyOf(kind, id)] = { ...pending[keyOf(kind, id)], soft: true }
          const draftEntry = entry(kind, id)
          if (
            !hasWorkDraftInput(after[id] as Chat) &&
            !draftEntry.flight &&
            !pending[keyOf(kind, id)]?.uncertain &&
            !pending[keyOf(kind, id)]?.creating &&
            !pending[keyOf(kind, id)]?.serverId
          ) {
            clearTimeout(draftEntry.timer)
            draftEntry.timer = undefined
            draftEntry.dirty = false
            draftEntry.failed = false
            refreshError()
            continue
          }
        }
        if (!(kind === 'chat' && id === WORK_DRAFT_ID)) {
          const key = keyOf(kind, id)
          if (!pending[key]) {
            let base: SaveBody | undefined
            try {
              if (before[id]) base = bodyOf(kind, before[id])
            } catch {
              /* 이전 입력이 유효하지 않으면 수동 비교한다. */
            }
            pending[key] = { base }
          }
        }
        useSyncStatus.setState({ savedAt: null })
        const e = entry(kind, id)
        e.dirty = true
        if (e.conflict || pending[keyOf(kind, id)]?.conflict) {
          e.conflict = true
          e.failed = true
          continue
        }
        if (e.uncertain) {
          e.failed = true
          refreshError(
            '이 항목의 생성 결과가 불확실해요. 서버 목록을 확인하기 전에는 다시 만들지 않아요.'
          )
          continue
        }
        // 새 입력은 새 저장이다 — 앞선 실패와 상관없이 보낸다(같은 요청을 다시 보내는 게 아니다)
        e.failed = false
        if (e.flight) continue
        schedule(kind, id)
      }
      for (const id of Object.keys(before)) {
        if (id in after) continue
        pending[keyOf(kind, id)] = { deleted: true }
        useSyncStatus.setState({ savedAt: null })
        const e = entries.get(keyOf(kind, id))
        clearTimeout(e?.timer)
        if (!e?.flight) void removeRemote(kind, id)
      }
    }
  })
  // 창을 닫거나 다른 탭으로 갈 때는 기다리지 않고 바로 보낸다(5-8)
  const now = () => {
    void flushAll().catch(() => {})
  }
  window.addEventListener('pagehide', now)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') now()
  })
}

/** 편집을 마치기 전에 기다리는 저장을 모두 지금 보낸다. */
export async function flushAll(
  keys?: readonly string[],
  { retryFailed = false }: { retryFailed?: boolean } = {}
) {
  const selected = () => {
    const resolved = keys?.map((key) => {
      const separator = key.indexOf(':')
      const kind = key.slice(0, separator) as Kind
      return keyOf(kind, studioServerId(kind, key.slice(separator + 1)))
    })
    return [...entries.entries()].filter(
      ([key]) => !keys || keys.includes(key) || resolved?.includes(key)
    )
  }
  if (retryFailed) {
    for (const [, e] of selected()) {
      if (e.failed && !e.uncertain && !e.conflict) {
        e.failed = false
        e.dirty = true
      }
    }
  }
  for (let attempt = 0; attempt < 5; attempt += 1) {
    const waiting = selected().filter(
      ([, e]) => e.timer || e.flight || (e.dirty && !e.failed && !e.uncertain)
    )
    if (!waiting.length) break
    await Promise.all(
      waiting.map(([key]) =>
        flush(
          key.slice(0, key.indexOf(':')) as Kind,
          key.slice(key.indexOf(':') + 1)
        )
      )
    )
  }
  if (selected().some(([, e]) => e.failed || e.uncertain))
    throw new Error(
      useSyncStatus.getState().error || '저장하지 못한 편집이 있어요.'
    )
  if (selected().some(([, e]) => e.timer || e.flight || e.dirty))
    throw new Error('아직 저장 중인 편집이 있어요. 잠시 후 다시 시도해 주세요.')
}

/** 일반 실패만 다시 보낸다. 저장 충돌은 비교 화면에서 해결한다. */
export function retryFailed(keys?: readonly string[]) {
  if (loadingFailed) {
    if (owner)
      void loadStudio(owner, failedKinds).catch((error: unknown) =>
        useSyncStatus.setState({ error: messageOf(error), savedAt: null })
      )
    return
  }
  if (
    [...entries.entries()].some(
      ([key, e]) => e.uncertain && (!keys || keys.includes(key))
    )
  ) {
    if (owner)
      void loadStudio(owner).catch((error: unknown) =>
        useSyncStatus.setState({ error: messageOf(error), savedAt: null })
      )
    return
  }
  for (const [key, e] of entries) {
    if (!e.failed || e.conflict || (keys && !keys.includes(key))) continue
    const kind = key.slice(0, key.indexOf(':')) as Kind
    const id = key.slice(key.indexOf(':') + 1)
    if (pending[key]?.deleted) {
      void removeRemote(kind, id)
      continue
    }
    e.failed = false
    e.dirty = true
    void flush(kind, id)
  }
}

/** 작품 저장을 확정한다. 내부 심사/홈 노출을 요청하거나 로컬에서 승인 상태를 만들지 않는다. */
export async function saveWork(requestedId: string) {
  const context = contextOf()
  const assertOwner = () => {
    if (
      !context ||
      !current(context) ||
      useRenewStore.getState().owner !== context.owner
    )
      throw new Error('계정이 바뀌어 저장을 중단했어요.')
  }
  assertOwner()
  const id = await savedWorkId(requestedId)
  assertOwner()
  const work = entity('chat', id) as Chat | undefined
  if (!work) throw new Error('저장할 작품을 찾지 못했어요.')
  if (isStudioWorkLocked(work))
    throw new Error('현재 서버에서 편집이 잠긴 작품이에요.')
  const related = [
    keyOf('chat', id),
    ...refsOf('chat', work).map(([kind, refId]) => keyOf(kind, refId)),
  ]
  await flushAll(related)
  assertOwner()
  if (
    !entity('chat', id) ||
    related.some((key) => pending[key]) ||
    entry('chat', id).failed
  ) {
    throw new Error(
      useSyncStatus.getState().error ||
        '서버에 확인되지 않은 편집이 있어요. 저장 상태를 확인해 주세요.'
    )
  }
  const sid = serverId('chat', id)
  if (!sid) throw new Error('저장된 작품 ID를 확인하지 못했어요.')
  // 기존 약관 버전별 동의 기록은 보존한다. 저장 성공과 홈 공개는 별개의 서버 판단이다.
  if ((entity('chat', id) as Chat | undefined)?.consent) {
    const terms = await api<{ configured: boolean; version: string }>(
      '/terms/post-delete/'
    )
    assertOwner()
    if (terms.configured) {
      const response = await api<ServerWork>(`${PATH.chat}${sid}/consent/`, {
        version: terms.version,
        agreed: true,
      })
      assertOwner()
      if (response.id !== sid || !response.revision)
        throw new Error(
          '동의 저장 결과를 확인하지 못했어요. 작품을 다시 열어 주세요.'
        )
      const latest = useRenewStore.getState().chats[id]
      if (latest)
        silently(() =>
          useRenewStore.getState().updateChat(id, {
            revision: response.revision,
            ...(internalReviewSchema.safeParse(response.internalReview).success
              ? {
                  internalReview: internalReviewSchema.parse(
                    response.internalReview
                  ),
                }
              : {}),
            ...(publicationSchema.safeParse(response.publication).success
              ? { publication: publicationSchema.parse(response.publication) }
              : {}),
          })
        )
    }
  }
  return { workId: sid }
}
